/**
 * Signals tab PR 1 — QA batch 3, D-C: the one shared module that drives
 * every group header — empty-value labels, the Gong-flow-name resolver, and
 * the Step #/Local time/Business hours label formatters. Pure (no server or
 * client-only imports) so both `signalGroupValue.ts` (client, G2) and any
 * future server-side consumer read the SAME strings Action View already
 * shows reps (`mini-apps/action-view/grouping.ts`), never a re-derived copy.
 *
 * `NO_STEP_DATA_LABEL` is imported, not redeclared here — it is
 * `signal-verification.ts`'s own export (batch 1 D-E / batch 2 D-J) and
 * `step_number` uses it as its blank-group label (`signal_tactic`, the "Step type" field, reads "No step type").
 */
import { NO_STEP_DATA_LABEL } from './signal-verification';
import { SIGNAL_PASSTHROUGH_FIELDS } from './signal-view';

/**
 * Every Action View group's EMPTY-value label, keyed by the Signals field
 * (or `SIGNAL_SYNTHETIC_GROUP_KEYS` member) that group is derived from.
 * Verbatim from `getGroupValue` (`mini-apps/action-view/grouping.ts:118-140`)
 * except `local_time`, whose real (non-empty) label deliberately deviates
 * from Action View's minute-exact `getLocalTime` (D-B, OQ1) — the EMPTY
 * label ('Unknown') still matches Action View's.
 */
const HAND_WRITTEN_EMPTY_LABELS: Readonly<Record<string, string>> = {
  assignee: 'Unassigned',           signal_source_type: 'No type',   signal_source_subtype: 'No subtype',
  campaign_name: 'No campaign',     signal_source: 'No signal source',      assignee_role: 'No role',
  account_sales_tier: 'No tier',    account_fit: 'No fit score',     contact_persona: 'No persona',
  contact_stage: 'No stage',        flow_name: 'No Gong flow',            account_industry: 'No industry',
  account_product_tier: 'No product tier',
  step_number: NO_STEP_DATA_LABEL,  signal_tactic: 'No step type',
  local_time: 'Unknown',            business_hours: 'No location data',
  // copied from mini-apps/action-view/grouping.ts:118-140 (17 keys above)

  // fix-plan-batch3.md D-F — offered by Terra's OWN Group-by picker
  // derivation (`groupableColumnsOf`), not by Action View's picker (QA
  // item 42): "No <column label>" in Action View's Title Case, except
  // `action_completed_by_name` ("Not Closed" — a person column whose blank
  // means the task is still open; "No Closed By" is not English).
  contact_name: 'No contact',
  contact_title: 'No title',
  contact_mailing_city: 'No city',
  account_name: 'No account',
  account_email_domain_link: 'No domain',
  account_billing_country: 'No country',
  signal_status: 'No Gong flow status',
  action_state: 'No action status',
  action_reason: 'No archive reason',
  action_completed_by_name: 'Not closed',

  // Signals V3 PR 1, U4 — the account's owner / ClayDR / Growth Strategist.
  account_owner_name: 'No account owner',
  account_claydr_name: 'No account ClayDR',
  account_gs_name: 'No growth strategist',
};

/**
 * Signals V3 PR 1, U3 — the ~80 generated Contact/Account passthrough fields
 * get a default "No <column label>" empty-group label (e.g. "No account
 * region"), so grouping by any of them never prints a bare "—" header. A
 * hand-written (Action View parity) label always wins over a generated one.
 */
/** Words that stay capitalised mid-label: Clay / Salesforce vocabulary that is a proper noun. */
const PROPER_NOUN_WORDS: ReadonlySet<string> = new Set(['Gong', 'Clay', 'Salesforce', 'Playhouse', 'Terra']);

/**
 * Sentence case for a generated label: the first word is kept, every later
 * plain Title Case word (`/^[A-Z][a-z]+$/`) is lowercased unless it is a
 * proper noun. Acronyms and mixed case (`ICP`, `ClayDR`, `L90`) are untouched.
 */
export function sentenceCaseLabel(text: string): string {
  return text
    .split(' ')
    .map((word, i) => (i > 0 && /^[A-Z][a-z]+$/.test(word) && !PROPER_NOUN_WORDS.has(word) ? word.toLowerCase() : word))
    .join(' ');
}

const GENERATED_EMPTY_LABELS: Readonly<Record<string, string>> = Object.fromEntries(
  SIGNAL_PASSTHROUGH_FIELDS.map(f => [f.key, sentenceCaseLabel(`No ${f.label}`)]),
);

export const SIGNAL_GROUP_EMPTY_LABELS: Readonly<Record<string, string>> = {
  ...GENERATED_EMPTY_LABELS,
  ...HAND_WRITTEN_EMPTY_LABELS,
};

export const UNRESOLVED_FLOW_LABEL = 'Unresolved Flow';

/** Detect Gong flow names that are actually raw SFDC record IDs (Gong sync bug). */
// copied from mini-apps/action-view/utils.ts:162
const SFDC_ID_RE = /^[a-zA-Z0-9]{15,18}$/;

/** copied from mini-apps/action-view/utils.ts:162-166 */
export function resolveFlowName(name: string | null | undefined): string | null {
  if (!name) return null;
  if (SFDC_ID_RE.test(name) && name.startsWith('a')) return UNRESOLVED_FLOW_LABEL;
  return name;
}

/** `Step ${n}` — mini-apps/action-view/grouping.ts:130. */
export function stepNumberGroupLabel(n: number): string {
  return `Step ${n}`;
}

/** Fixed open → closed → unknown order (D-B: a deliberate deviation from
 *  Action View's size-ordered groups — noted, not an open question). */
export const BUSINESS_HOURS_GROUP_LABELS = {
  open: 'In business hours',
  closed: 'Outside business hours',
  unknown: 'No location data',
} as const;

const HOUR_LABEL_FORMAT = new Intl.DateTimeFormat('en-US', { hour: 'numeric', hour12: true, timeZone: 'UTC' });

/**
 * The 24 hour-bucket labels ('12 AM' … '11 PM'), index = hour 0–23. Computed
 * ONCE against a fixed UTC reference day (fix-plan-batch3.md D-E, F6/F7):
 *
 *  - F7 — the OLD implementation formatted `now.setHours(hour)` in the
 *    VIEWER's zone; on a spring-forward day (2026-03-08 in
 *    America/New_York) the skipped hour folds onto the next, so hours 2
 *    AND 3 both format as "3 AM" — two Local-time buckets share one header,
 *    and the numeric sort key (`hour`) disagrees with the label.
 *  - F6 — the OLD implementation built a new `Intl.DateTimeFormat` on every
 *    call; FlowTable calls `groupValue` ~4x per row per render, measured at
 *    170.6 ms / 15,200 calls locally (a 24-entry lookup: 0.58 ms).
 *
 * Formatting in UTC against a fixed date removes both the viewer's zone and
 * the calendar day from the computation entirely — there is no DST
 * transition to fold across, and the array is built once at module load.
 */
export const LOCAL_HOUR_GROUP_LABELS: readonly string[] = Object.freeze(
  Array.from({ length: 24 }, (_, h) => HOUR_LABEL_FORMAT.format(new Date(Date.UTC(2000, 0, 1, h)))),
);

/** `hour` is expected to be an integer 0–23 (a `LocalTimeResult.hour`); any
 *  other value (out of range, fractional, `NaN`) reads `Unknown` — same
 *  fallback as a `LocalTimeResult` with no `hour` at all. */
export function localHourGroupLabel(hour: number): string {
  return LOCAL_HOUR_GROUP_LABELS[hour] ?? SIGNAL_GROUP_EMPTY_LABELS.local_time;
}
